import { isNil } from 'lodash/fp';
import { Component } from 'react';
import { IconName, withBreakpointRange } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey, ActionType, DataTestId } from '@mews-commander/enums';
import { ModalBodyWrapper, ModalWrapper, WithBreakpointRange, isMobileOrTablet } from '@mews-commander/ui';
import { WithGuideLink } from '@mews-commander/types';
import MembersPanel from '../../containers/MembersPanel';
import MessagesPanel from '../../containers/MessagesPanel';
import { SendMessage } from '../../containers/SendMessage';
import ThreadsPanel from '../../containers/ThreadsPanel';
import { Member, Thread } from '../../types';
import { isThreadPosted } from '../../utils';
import { MessagesBlockWrapper, MessagesContainerElement, MessagesWrapperElement } from '../style';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import { HeadingAction } from '../../../../legacy/React/Components/HeadingAction/Action/types';
import { getHasHistory } from '../../../../core/history';

type BaseProps = WithBreakpointRange & Partial<WithGuideLink>;

interface MessagesProps extends BaseProps {
    currentThreadId: string;
    currentThread: Thread;
    currentThreadDraft: string;
    currentThreadMembers: Member[];
    initialThreadId?: string;
    onSendMessage: (threadId: string, text: string) => void;
    onPostThread: (thread: Thread, text: string, members: Member[]) => void;
    onMount: () => void;
    onUnmount: () => void;
    onInit: ({ threadId }: { threadId?: string }) => void;
    handleClose: () => void;
}

class MessagesApp extends Component<MessagesProps> {
    componentDidMount() {
        this.props.onMount();
        const { initialThreadId } = this.props;
        this.props.onInit({ threadId: initialThreadId });
    }

    componentWillUnmount() {
        this.props.onUnmount();
    }

    handleSendMessage = () => {
        const { currentThread, currentThreadMembers, currentThreadDraft, onSendMessage, onPostThread } = this.props;
        if (isThreadPosted(currentThread)) {
            onSendMessage(currentThread.id, currentThreadDraft);
        } else {
            onPostThread(currentThread, currentThreadDraft, currentThreadMembers);
        }
    };

    render() {
        const { currentThreadId, currentThread, breakpointRange, handleClose, guideLink } = this.props;

        const isSmall = isMobileOrTablet(breakpointRange);
        const showMessageDetails = !isNil(currentThread);

        /* MessagesApp component is also used in a BE template where guide link is not sent
           search for commander.renderContainer('MessagesApp' in the BE repo
         */
        const actions: HeadingAction[] =
            guideLink != null
                ? [
                      {
                          type: ActionType.Link,
                          actionId: 'help',
                          text: <LocalizedText textKey={TextKey.Help} />,
                          icon: IconName.Help,
                          target: '_blank',
                          url: guideLink,
                      },
                  ]
                : [];

        const showCloseButton = getHasHistory();
        const primaryAction: HeadingAction | null = showCloseButton
            ? {
                  type: ActionType.Link,
                  icon: IconName.CrossClose,
                  'data-test-icon-button': IconName.CrossClose,
                  onClick: handleClose,
              }
            : null;

        return (
            <ModalWrapper>
                <ModalHeader
                    title={<LocalizedText textKey={TextKey.MessagePlural} />}
                    actions={actions}
                    primaryAction={primaryAction}
                />
                <ModalBodyWrapper>
                    <MessagesContainerElement data-test-id={DataTestId.MessagesContainer}>
                        <MessagesWrapperElement>
                            <ThreadsPanel />
                            {showMessageDetails && (
                                <>
                                    <MessagesBlockWrapper isSmall={isSmall} isActive={currentThreadId !== null}>
                                        <MessagesPanel />
                                        <SendMessage onSend={this.handleSendMessage} />
                                    </MessagesBlockWrapper>
                                    <MembersPanel />
                                </>
                            )}
                        </MessagesWrapperElement>
                    </MessagesContainerElement>
                </ModalBodyWrapper>
            </ModalWrapper>
        );
    }
}

export default withBreakpointRange(MessagesApp);
